import { useState } from 'react';
import { ArrowRight, ArrowUpRight, Check, Calendar, Mail, User, Building, MessageSquare, Sparkles, Play, Users, Clock, Headphones } from 'lucide-react';
import SEO from '@/components/SEO';
import { site } from '@/data/content';

const whyBook = [
  { icon: Play, title: 'See it live', text: 'Watch your channels, AI, and reports in action.' },
  { icon: Headphones, title: 'Ask an expert', text: 'Get honest answers, no scripts or pressure.' },
  { icon: Users, title: 'Get a tailored plan', text: 'We match the right setup to your team size.' },
  { icon: Clock, title: 'Save time', text: '30 minutes that speeds up your rollout.' },
];

const whatToExpect = [
  { step: '1', title: 'Pick a time', text: 'Choose a 30-minute slot.' },
  { step: '2', title: 'Live walkthrough', text: 'We show the inbox, AI, and reporting for your use case.' },
  { step: '3', title: 'Q&A and next steps', text: 'You ask questions; we send pricing and a setup note.' },
];

const whatYouWillSee = [
  { icon: 'Inbox', title: 'Unified inbox', text: 'WhatsApp, email, chat, Instagram, and X in one queue.' },
  { icon: 'Sparkles', title: 'Zyra AI', text: 'Draft replies, summarize threads, and deflect FAQs.' },
  { icon: 'BarChart3', title: 'Live reports', text: 'Response time, CSAT, resolution, and workload.' },
  { icon: 'Shield', title: 'Team & security', text: 'Roles, permissions, and audit logs for your org.' },
];

export default function BookDemo() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    note: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = `Demo request from ${form.company || form.name}`;
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Company: ${form.company}`,
      `Phone: ${form.phone || 'Not provided'}`,
      '',
      'What they want to see:',
      form.note || 'No details provided',
    ].join('\n');
    window.location.href = `mailto:sales@zurihub.io?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <>
      <SEO
        title="Book a Demo"
        description="Book a personalized ZuriHub demo with our team. See omnichannel AI, live chat, and voice in action."
        path="/book-a-demo"
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-28 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
            <Calendar className="h-4 w-4" /> 30 minutes. No pressure.
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Book a <span className="gradient-text">demo</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
            See how ZuriHub unifies your channels and speeds up replies with AI.
          </p>
        </div>
      </section>

      <section className="py-16" aria-label="Book a demo">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-10 lg:grid-cols-2">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">Why book a demo?</h2>
              <p className="mt-3 text-slate-600">
                A demo is the fastest way to see how ZuriHub fits your team.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {whyBook.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-5"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">{item.title}</h3>
                        <p className="mt-1 text-sm text-slate-600">{item.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-10">
                <h3 className="text-xl font-bold text-slate-900">What to expect</h3>
                <div className="mt-5 space-y-3">
                  {whatToExpect.map((s) => (
                    <div
                      key={s.step}
                      className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-extrabold text-brand-700">
                        {s.step}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900">{s.title}</h4>
                        <p className="text-sm text-slate-600">{s.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:sticky lg:top-24">
              {submitted ? (
                <div className="rounded-3xl border border-green-200 bg-green-50 p-8 text-center shadow-sm">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
                    <Check className="h-7 w-7" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-slate-900">Request ready to send</h3>
                  <p className="mt-2 text-sm text-slate-600">
                    Your email client is open. Send the message to sales@zurihub.io and we will confirm within one business day.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: '', email: '', company: '', phone: '', note: '' });
                    }}
                    className="mt-5 rounded-full bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/50"
                >
                  <h3 className="text-xl font-bold text-slate-900">Request your demo</h3>
                  <p className="mt-1 text-sm text-slate-600">Fill in your details and we will be in touch.</p>

                  <div className="mt-5 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="block text-xs font-semibold text-slate-700">Name</label>
                        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                          <User className="h-4 w-4 text-slate-400" />
                          <input
                            id="name"
                            type="text"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400"
                            placeholder="Jane Doe"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-xs font-semibold text-slate-700">Company</label>
                        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                          <Building className="h-4 w-4 text-slate-400" />
                          <input
                            id="company"
                            type="text"
                            required
                            value={form.company}
                            onChange={(e) => setForm({ ...form, company: e.target.value })}
                            className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400"
                            placeholder="Acme Inc."
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-semibold text-slate-700">Work email</label>
                      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <input
                          id="email"
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400"
                          placeholder="jane@company.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-xs font-semibold text-slate-700">Phone <span className="font-normal text-slate-400">(optional)</span></label>
                      <input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                        placeholder="+1 234 567 890"
                      />
                    </div>
                    <div>
                      <label htmlFor="note" className="block text-xs font-semibold text-slate-700">What would you like to see? <span className="font-normal text-slate-400">(optional)</span></label>
                      <div className="mt-1.5 flex gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                        <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                        <textarea
                          id="note"
                          rows={2}
                          value={form.note}
                          onChange={(e) => setForm({ ...form, note: e.target.value })}
                          className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400"
                          placeholder="Your goals or channels..."
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 px-6 py-3 text-sm font-bold text-white transition hover:bg-brand-700"
                  >
                    Send demo request <ArrowRight className="h-4 w-4" />
                  </button>
                  <p className="mt-3 text-center text-xs text-slate-500">
                    By submitting, you agree to our Terms and Privacy. Request is emailed to sales@zurihub.io.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" aria-labelledby="see-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 id="see-heading" className="section-title">What you will see on the demo</h2>
            <p className="section-subtitle mx-auto mt-3">
              A focused walkthrough of the features that matter to you.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {whatYouWillSee.map((item) => (
              <div
                key={item.title}
                className="flex flex-col items-start rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                  <Sparkles className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-slate-900">{item.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" aria-label="Try for free">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Not ready for a demo?</h2>
          <p className="mx-auto mt-3 max-w-xl text-slate-600">
            Start a 14-day free trial and explore ZuriHub with your team.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-bold text-white transition hover:bg-slate-800"
            >
              Start free trial <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              href="mailto:sales@zurihub.io"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-brand-300 hover:text-brand-700"
            >
              Email sales <Mail className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
